import { Star } from "lucide-react";

const stats = [
  {
    number: "10",
    label: "Years Building AI That Actually Teaches",
    description:
      "Not just engineers. We built Shattara with educators, tutors, and students — because the best AI tutor needs to understand learning, not just language.",
    variant: "light" as const,
  },
  {
    number: "120",
    label: "Hours Saved Per Student, Per Semester",
    description:
      "That's 5 full days back every semester. Imagine what you could do with 120 extra hours — more sleep, more life, same grades.",
    variant: "dark" as const,
  },
  {
    number: "35",
    label: "Ways to Help You Win at School",
    description:
      "Quizzes that adapt to your weak spots. Flashcards that remember what you forget. A tutor that's never tired, never impatient, always on.",
    variant: "light" as const,
  },
];

const students = [
  {
    id: 1,
    name: "Student 1",
    image: "/images/avatars/student-1.jpg",
  },
  {
    id: 2,
    name: "Student 2",
    image: "/images/avatars/student-2.jpg",
  },
  {
    id: 3,
    name: "Student 3",
    image: "/images/avatars/student-3.jpg",
  },
  {
    id: 4,
    name: "Student 4",
    image: "/images/avatars/student-4.jpg",
  },
];

const institutions = [
  {
    name: "King Abdulaziz University",
    logo: "/images/logos/king-abdulaziz.png",
  },
  {
    name: "KAUST",
    logo: "/images/logos/kaust.png",
  },
  {
    name: "Prince Mohammad Bin Fahd University",
    logo: "/images/logos/pmu.png",
  },
  {
    name: "Umm Al-Qura University",
    logo: "/images/logos/umm-al-qura.png",
  },
  {
    name: "MIT",
    logo: "/images/logos/mit.png",
  },
  {
    name: "Yale",
    logo: "/images/logos/yale.png",
  },
  {
    name: "Princeton",
    logo: "/images/logos/princeton.png",
  },
  {
    name: "Berkeley",
    logo: "/images/logos/berkeley.png",
  },
  {
    name: "Princess Nourah Bint Abdulrahman University",
    logo: "/images/logos/princess-nourah.png",
  },
];

export const WhyUs = () => {
  return (
    <section id="why-us" className="w-full bg-muted py-24 px-6 md:px-10">
      <div className="bg-foreground rounded-3xl py-24 px-8 md:py-32 md:px-16 mx-auto">
        <div className="flex flex-col gap-[3.75rem]">
          {/* Header Section */}
          <div className="flex flex-col items-center gap-5">
            <h2 className="text-4xl leading-none text-primary-foreground text-center font-medium md:text-6xl">
              Why Students Choose Shattara Over Everything Else
            </h2>
            <p className="text-primary-foreground/70 font-medium max-w-[37.5rem] text-center">
              Built by educators and AI researchers who were tired of watching smart students fail because of bad study
              habits — not because they weren't capable.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Stat Cards */}
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`rounded-2xl p-7 flex flex-col gap-[6.25rem] ${
                  stat.variant === "light" ? "bg-muted" : "bg-primary-foreground/5"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center gap-1.5 font-medium">
                    <h3
                      className={`text-[3.38rem] leading-none ${
                        stat.variant === "light" ? "text-foreground" : "text-primary-foreground"
                      }`}
                    >
                      {stat.number}
                    </h3>
                    <p
                      className={`text-[3.5rem] leading-none ${
                        stat.variant === "light" ? "text-foreground" : "text-primary-foreground"
                      }`}
                    >
                      +
                    </p>
                  </div>
                  <p
                    className={`font-semibold flex-1 ${
                      stat.variant === "light" ? "text-foreground" : "text-primary-foreground"
                    }`}
                  >
                    {stat.label}
                  </p>
                </div>
                <p
                  className={`font-medium max-w-[18.75rem] ${
                    stat.variant === "light" ? "text-foreground/75" : "text-primary-foreground/70"
                  }`}
                >
                  {stat.description}
                </p>
              </div>
            ))}

            {/* Happy Students Card - Full Width on Mobile */}
            <div className="bg-primary-foreground/5 rounded-2xl p-7 md:col-span-2 lg:col-span-3 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-[6.25rem]">
              <div className="flex-1 flex flex-wrap items-center gap-7">
                {/* Student Avatars */}
                <div className="flex -space-x-2">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      className="w-11 h-11 rounded-lg border-2 border-foreground overflow-hidden bg-muted"
                    >
                      <img src={student.image} alt={student.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>

                {/* Rating */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow text-yellow" />
                    ))}
                  </div>
                  <p className="text-sm text-primary-foreground font-semibold">
                    5-star rated. Saudi Arabia's fastest-growing AI study platform.
                  </p>
                </div>
              </div>

              {/* Student Count */}
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-1.5 font-medium">
                  <h3 className="leading-none font-semibold text-4xl text-primary-foreground">5K</h3>
                  <p className="leading-none font-semibold text-4xl text-primary-foreground">+</p>
                </div>
                <p className="font-semibold text-primary-foreground">Active Students</p>
              </div>
            </div>
          </div>

          {/* Institution Logos Marquee */}
          <div className="flex flex-col items-center gap-7 max-w-[56.25rem] mx-auto w-full">
            <p className="text-primary-foreground/70 font-medium text-center">Trusted by students from:</p>
            <div className="relative w-full overflow-hidden">
              <div className="flex items-center gap-9 [mask-image:linear-gradient(to_right,transparent,black_20%,black_80%,transparent)]">
                <div className="flex items-center gap-9 animate-marquee-horizontal">
                  {institutions.map((institution, index) => (
                    <div
                      key={index}
                      className="bg-muted rounded-lg w-36 h-16 flex items-center justify-center flex-shrink-0 p-2"
                    >
                      <img
                        src={institution.logo}
                        alt={institution.name}
                        className="w-full h-full object-contain scale-125"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-9 animate-marquee-horizontal" aria-hidden="true">
                  {institutions.map((institution, index) => (
                    <div
                      key={`duplicate-${index}`}
                      className="bg-muted rounded-lg w-36 h-16 flex items-center justify-center flex-shrink-0 p-2"
                    >
                      <img
                        src={institution.logo}
                        alt={institution.name}
                        className="w-full h-full object-contain scale-125"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
